import React, { useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { FeatureBlock } from '../ui/feature-block';
import { SectionHeading } from '../ui/section-heading';

const PURPLE = '#7F03A1';
const PURPLE_LIGHT = 'rgba(127,3,161,0.08)';

/* ────────────────────────────────────────────────────────────
   Panel 1  ·  Scattered Tools → Circle → Shift Left → Branch
   ──────────────────────────────────────────────────────────── */

const pillData = [
  { label: 'ChatGPT',     sx: 5,   sy: 8,   dx: 100, dy: 108 },
  { label: 'Claude',      sx: 185, sy: 5,   dx: -32, dy: 105 },
  { label: 'n8n',         sx: 290, sy: 8,   dx: -108, dy: 108 },
  { label: 'Midjourney',  sx: 305, sy: 100, dx: -120, dy: 30  },
  { label: 'Gemini',      sx: 285, sy: 210, dx: -102, dy: -45 },
  { label: 'Claude Code', sx: 175, sy: 250, dx: -22, dy: -82  },
  { label: 'Cursor',      sx: 65,  sy: 250, dx: 55,  dy: -88  },
  { label: 'Lovable',     sx: 0,   sy: 210, dx: 105, dy: -65  },
  { label: 'Evolog',      sx: 0,   sy: 100, dx: 110, dy: 20   },
  { label: 'SlideWithAI', sx: 85,  sy: 0,   dx: 50,  dy: 132  },
];

const CX = 165;
const CY = 140;
const CR = 108;

const ToolConvergePanel: React.FC = () => {
  const wrap     = useAnimation();
  const pill     = useAnimation();
  const circle   = useAnimation();
  const glow     = useAnimation();
  const stem     = useAnimation();
  const b1       = useAnimation();
  const b2       = useAnimation();
  const b3       = useAnimation();
  const b4       = useAnimation();

  useEffect(() => {
    const delay = (ms: number) => new Promise(r => setTimeout(r, ms));
    const seq = async () => {
      while (true) {
        /* ── Reset ── */
        wrap.set({ x: 0 });
        pill.set({ x: 0, y: 0, opacity: 0, scale: 1 });
        circle.set({ pathLength: 0, opacity: 0 });
        glow.set({ opacity: 0 });
        stem.set({ pathLength: 0, opacity: 0 });
        b1.set({ pathLength: 0, opacity: 0 });
        b2.set({ pathLength: 0, opacity: 0 });
        b3.set({ pathLength: 0, opacity: 0 });
        b4.set({ pathLength: 0, opacity: 0 });
        await delay(300);

        /* ── 1  Pills appear scattered ── */
        await pill.start((i: number) => ({
          opacity: 1,
          transition: { duration: 0.35, delay: i * 0.07, ease: 'easeOut' },
        }));
        await delay(600);

        /* ── 2  Big circle draws ── */
        await circle.start({
          pathLength: 1, opacity: 1,
          transition: { duration: 1.4, ease: [0.33, 1, 0.68, 1] },
        });
        await delay(200);

        /* ── 3  Pills converge inside ── */
        await pill.start((i: number) => ({
          x: pillData[i].dx,
          y: pillData[i].dy,
          scale: 0.82,
          transition: {
            type: 'spring', stiffness: 80, damping: 14,
            delay: i * 0.04,
          },
        }));

        /* ── Glow behind circle ── */
        glow.start({ opacity: 1, transition: { duration: 0.6 } });
        await delay(350);

        /* ── 4  Shift everything left ── */
        await wrap.start({
          x: -50,
          transition: { type: 'spring', stiffness: 100, damping: 18 },
        });
        await delay(100);

        /* ── 5  Stem + branches ── */
        await stem.start({
          pathLength: 1, opacity: 1,
          transition: { duration: 0.3, ease: 'easeOut' },
        });
        b1.start({ pathLength: 1, opacity: 1, transition: { duration: 0.35, ease: 'easeOut' } });
        b2.start({ pathLength: 1, opacity: 1, transition: { duration: 0.35, ease: 'easeOut', delay: 0.08 } });
        await b3.start({ pathLength: 1, opacity: 1, transition: { duration: 0.35, ease: 'easeOut', delay: 0.16 } });

        /* ── 7  Second layer branches ── */
        await delay(150);
        await b4.start({ pathLength: 1, opacity: 1, transition: { duration: 0.3, ease: 'easeOut' } });

        /* ── Hold ── */
        await delay(2800);

        /* ── Fade out ── */
        await Promise.all([
          pill.start({ opacity: 0, transition: { duration: 0.5, ease: 'easeIn' } }),
          circle.start({ opacity: 0, transition: { duration: 0.5 } }),
          glow.start({ opacity: 0, transition: { duration: 0.5 } }),
          stem.start({ opacity: 0, transition: { duration: 0.4 } }),
          b1.start({ opacity: 0, transition: { duration: 0.4 } }),
          b2.start({ opacity: 0, transition: { duration: 0.4 } }),
          b3.start({ opacity: 0, transition: { duration: 0.4 } }),
          b4.start({ opacity: 0, transition: { duration: 0.4 } }),
        ]);
        await delay(400);
        pill.set({ x: 0, y: 0, scale: 1 });
      }
    };
    seq();
  }, [wrap, pill, circle, glow, stem, b1, b2, b3, b4]);

  const RX = CX + CR;

  return (
    <div className="w-full flex items-center justify-center overflow-hidden">
      <motion.div animate={wrap} className="relative" style={{ width: 380, height: 280 }}>

        {/* Glow behind circle when pills are inside */}
        <motion.div
          animate={glow}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: CX - CR - 20, top: CY - CR - 20,
            width: (CR + 20) * 2, height: (CR + 20) * 2,
            background: `radial-gradient(circle, ${PURPLE_LIGHT} 0%, transparent 70%)`,
          }}
          initial={{ opacity: 0 }}
        />

        {/* Pills */}
        {pillData.map((p, i) => (
          <motion.div
            key={p.label}
            custom={i}
            animate={pill}
            className="absolute z-10"
            style={{ left: p.sx, top: p.sy }}
            initial={{ opacity: 0 }}
          >
            <span
              className="inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap border"
              style={i === 0
                ? { background: PURPLE, color: '#fff', borderColor: PURPLE, boxShadow: `0 2px 8px ${PURPLE}40` }
                : { background: '#fff', color: '#555', borderColor: '#e5e5e5', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }
              }
            >
              {p.label}
            </span>
          </motion.div>
        ))}

        {/* SVG overlay */}
        <svg className="absolute inset-0 overflow-visible z-20 pointer-events-none"
          width={380} height={280} viewBox="0 0 380 280" fill="none">

          {/* Circle */}
          <motion.circle
            animate={circle}
            cx={CX} cy={CY} r={CR}
            stroke={PURPLE} strokeWidth={2.5} strokeLinecap="round"
            fill="none" strokeDasharray="4 0"
            initial={{ pathLength: 0, opacity: 0 }}
            style={{ transformOrigin: `${CX}px ${CY}px`, rotate: -90 }}
          />

          {/* Stem from circle right edge */}
          <motion.line
            animate={stem}
            x1={RX} y1={CY} x2={RX + 40} y2={CY}
            stroke={PURPLE} strokeWidth={2.5} strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
          />

          {/* Branch dot */}
          <motion.circle
            animate={stem}
            cx={RX + 40} cy={CY} r={3}
            fill={PURPLE}
            initial={{ opacity: 0 }}
          />

          {/* Branch 1 — up then right (L-shape) */}
          <motion.path animate={b1}
            d={`M ${RX+40},${CY} L ${RX+40},${CY-55} L ${RX+130},${CY-55}`}
            stroke={PURPLE} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
          />
          <motion.polygon animate={b1}
            points={`${RX+136},${CY-55} ${RX+126},${CY-60} ${RX+126},${CY-50}`}
            fill={PURPLE} initial={{ opacity: 0 }}
          />

          {/* Branch 2 — slight up then right */}
          <motion.path animate={b1}
            d={`M ${RX+40},${CY} L ${RX+40},${CY-25} L ${RX+130},${CY-25}`}
            stroke={PURPLE} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
          />
          <motion.polygon animate={b1}
            points={`${RX+136},${CY-25} ${RX+126},${CY-30} ${RX+126},${CY-20}`}
            fill={PURPLE} initial={{ opacity: 0 }}
          />

          {/* Branch 3 — straight right */}
          <motion.path animate={b2}
            d={`M ${RX+40},${CY} L ${RX+130},${CY}`}
            stroke={PURPLE} strokeWidth={2} strokeLinecap="round"
            initial={{ pathLength: 0, opacity: 0 }}
          />
          <motion.polygon animate={b2}
            points={`${RX+136},${CY} ${RX+126},${CY-5} ${RX+126},${CY+5}`}
            fill={PURPLE} initial={{ opacity: 0 }}
          />

          {/* Branch 4 — slight down then right */}
          <motion.path animate={b3}
            d={`M ${RX+40},${CY} L ${RX+40},${CY+25} L ${RX+130},${CY+25}`}
            stroke={PURPLE} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
          />
          <motion.polygon animate={b3}
            points={`${RX+136},${CY+25} ${RX+126},${CY+20} ${RX+126},${CY+30}`}
            fill={PURPLE} initial={{ opacity: 0 }}
          />

          {/* Branch 5 — down then right (L-shape) */}
          <motion.path animate={b3}
            d={`M ${RX+40},${CY} L ${RX+40},${CY+55} L ${RX+130},${CY+55}`}
            stroke={PURPLE} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }}
          />
          <motion.polygon animate={b3}
            points={`${RX+136},${CY+55} ${RX+126},${CY+50} ${RX+126},${CY+60}`}
            fill={PURPLE} initial={{ opacity: 0 }}
          />
          {/* ── Second layer: sub-branches from each endpoint ── */}

          {/* From Branch 1 end (RX+130, CY-55) → up + down */}
          <motion.path animate={b4}
            d={`M ${RX+130},${CY-55} L ${RX+130},${CY-70} L ${RX+180},${CY-70}`}
            stroke={PURPLE} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }} />
          <motion.polygon animate={b4}
            points={`${RX+185},${CY-70} ${RX+177},${CY-74} ${RX+177},${CY-66}`}
            fill={PURPLE} initial={{ opacity: 0 }} />
          <motion.path animate={b4}
            d={`M ${RX+130},${CY-55} L ${RX+130},${CY-42} L ${RX+180},${CY-42}`}
            stroke={PURPLE} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }} />
          <motion.polygon animate={b4}
            points={`${RX+185},${CY-42} ${RX+177},${CY-46} ${RX+177},${CY-38}`}
            fill={PURPLE} initial={{ opacity: 0 }} />

          {/* From Branch 3 end (RX+130, CY) → up + down */}
          <motion.path animate={b4}
            d={`M ${RX+130},${CY} L ${RX+130},${CY-12} L ${RX+180},${CY-12}`}
            stroke={PURPLE} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }} />
          <motion.polygon animate={b4}
            points={`${RX+185},${CY-12} ${RX+177},${CY-16} ${RX+177},${CY-8}`}
            fill={PURPLE} initial={{ opacity: 0 }} />
          <motion.path animate={b4}
            d={`M ${RX+130},${CY} L ${RX+130},${CY+12} L ${RX+180},${CY+12}`}
            stroke={PURPLE} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }} />
          <motion.polygon animate={b4}
            points={`${RX+185},${CY+12} ${RX+177},${CY+8} ${RX+177},${CY+16}`}
            fill={PURPLE} initial={{ opacity: 0 }} />

          {/* From Branch 5 end (RX+130, CY+55) → up + down */}
          <motion.path animate={b4}
            d={`M ${RX+130},${CY+55} L ${RX+130},${CY+42} L ${RX+180},${CY+42}`}
            stroke={PURPLE} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }} />
          <motion.polygon animate={b4}
            points={`${RX+185},${CY+42} ${RX+177},${CY+38} ${RX+177},${CY+46}`}
            fill={PURPLE} initial={{ opacity: 0 }} />
          <motion.path animate={b4}
            d={`M ${RX+130},${CY+55} L ${RX+130},${CY+68} L ${RX+180},${CY+68}`}
            stroke={PURPLE} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"
            initial={{ pathLength: 0, opacity: 0 }} />
          <motion.polygon animate={b4}
            points={`${RX+185},${CY+68} ${RX+177},${CY+64} ${RX+177},${CY+72}`}
            fill={PURPLE} initial={{ opacity: 0 }} />
        </svg>
      </motion.div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────
   Panel 2  ·  Messy → Scanned → Clean Output
   ──────────────────────────────────────────────────────────── */

const docs = [
  { label: 'Vision',            color: '#7F03A1' },
  { label: 'Mission',           color: '#9333ea' },
  { label: 'Design System',     color: '#a855f7' },
  { label: 'Brand Guide',       color: '#c084fc' },
  { label: 'Component Library', color: '#d8b4fe' },
];

const VoicePanel: React.FC = () => {
  const docCtrl  = useAnimation();
  const voiceCtrl = useAnimation();

  useEffect(() => {
    const delay = (ms: number) => new Promise(r => setTimeout(r, ms));
    const seq = async () => {
      while (true) {
        docCtrl.set({ x: -50, opacity: 0 });
        voiceCtrl.set({ opacity: 0, x: 10 });
        await delay(300);

        /* Docs slide in from left and stack — slow enough to read each label */
        await docCtrl.start((i: number) => ({
          x: 0, opacity: 1,
          transition: { type: 'spring', stiffness: 90, damping: 14, delay: i * 0.55 },
        }));
        await delay(300);

        /* Waveform + lines appear beside the stack */
        await voiceCtrl.start({
          opacity: 1, x: 0,
          transition: { duration: 0.5, ease: 'easeOut' },
        });

        await delay(4500);

        /* Fade everything */
        await Promise.all([
          docCtrl.start({ opacity: 0, transition: { duration: 0.4 } }),
          voiceCtrl.start({ opacity: 0, transition: { duration: 0.4 } }),
        ]);
        await delay(400);
      }
    };
    seq();
  }, [docCtrl, voiceCtrl]);

  return (
    <div className="flex items-center gap-6 w-full justify-center">
      {/* Left: Document stack — 9:16 ratio panels */}
      <div className="relative flex-shrink-0" style={{ width: 150, height: 240 }}>
        {docs.map((doc, i) => (
          <motion.div
            key={doc.label}
            custom={i}
            animate={docCtrl}
            initial={{ x: -50, opacity: 0 }}
            className="absolute rounded-lg border border-gray-200 bg-white overflow-hidden"
            style={{
              width: 140,
              height: 178,
              left: i * 3,
              top: i * 12,
              zIndex: i,
              boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
            }}
          >
            {/* Colored header bar */}
            <div className="h-2.5" style={{ background: doc.color }} />
            {/* Doc content */}
            <div className="p-3 space-y-2">
              <p className="text-xs font-bold text-gray-800 leading-tight">{doc.label}</p>
              <div className="space-y-1.5">
                <div className="h-[3px] w-full rounded-full bg-gray-100" />
                <div className="h-[3px] w-4/5 rounded-full bg-gray-100" />
                <div className="h-[3px] w-3/5 rounded-full bg-gray-100" />
                <div className="h-[3px] w-5/6 rounded-full bg-gray-100" />
                <div className="h-[3px] w-2/3 rounded-full bg-gray-100" />
                <div className="h-[3px] w-4/5 rounded-full bg-gray-100" />
                <div className="h-[3px] w-1/2 rounded-full bg-gray-100" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Right: Voice waveform + output lines */}
      <motion.div
        animate={voiceCtrl}
        initial={{ opacity: 0, x: 10 }}
        className="flex flex-col gap-4 flex-1 min-w-0 max-w-[160px]"
      >
        {/* Waveform */}
        <div className="flex items-center justify-center gap-[2px] h-8">
          {Array.from({ length: 20 }).map((_, i) => {
            const h = [3, 5, 9, 6, 12, 4, 8, 14, 5, 11, 3, 7, 13, 6, 10, 4, 12, 7, 5, 9][i];
            return (
              <motion.div
                key={i}
                className="rounded-full"
                style={{ width: 2.5, height: h * 2, background: PURPLE, opacity: 0.15 + (h / 14) * 0.55 }}
                animate={{ scaleY: [1, 1.5, 0.6, 1.3, 1] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut', delay: i * 0.07 }}
              />
            );
          })}
        </div>

        {/* Output lines with checkmarks */}
        <div className="space-y-2">
          {[{ w: '88%', d: 0 }, { w: '95%', d: 0.25 }, { w: '75%', d: 0.5 }].map((line, i) => (
            <div key={i} className="flex items-center gap-2">
              <motion.div
                className="h-[5px] rounded-full"
                style={{ background: i === 0 ? PURPLE : '#d4d4d8', width: 0 }}
                animate={{ width: line.w }}
                transition={{ duration: 0.6, delay: 0.3 + line.d, ease: 'easeOut', repeat: Infinity, repeatDelay: 5 }}
              />
              <motion.div
                className="flex-shrink-0 w-3 h-3 rounded-full flex items-center justify-center"
                style={{ background: i === 0 ? PURPLE : '#e5e7eb' }}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.25, delay: 1 + i * 0.25, repeat: Infinity, repeatDelay: 5 }}
              >
                <svg width={8} height={8} viewBox="0 0 8 8" fill="none">
                  <path d="M 2 4 L 3.5 5.5 L 6 3" stroke="#fff" strokeWidth={1.2} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </motion.div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

/* ────────────────────────────────────────────────────────────
   Panel 3  ·  Automated Pipeline with Sequential Status
   ──────────────────────────────────────────────────────────── */

const pipelineSteps = [
  { label: 'Capture',    sub: 'Lead comes in' },
  { label: 'Qualify',    sub: 'AI scores fit' },
  { label: 'Follow up',  sub: 'Auto outreach' },
  { label: 'Onboard',   sub: 'Welcome flow' },
];

const WorkflowPanel: React.FC = () => {
  const stepCtrl = useAnimation();
  const dotCtrl  = useAnimation();

  useEffect(() => {
    const delay = (ms: number) => new Promise(r => setTimeout(r, ms));
    const seq = async () => {
      while (true) {
        stepCtrl.set({ opacity: 0, y: 8 });
        dotCtrl.set({ background: '#e5e7eb' });
        await delay(300);

        /* Steps slide in */
        await stepCtrl.start((i: number) => ({
          opacity: 1, y: 0,
          transition: { duration: 0.35, delay: i * 0.12, ease: 'easeOut' },
        }));
        await delay(400);

        /* Status dots light up one by one */
        for (let i = 0; i < pipelineSteps.length; i++) {
          dotCtrl.start((idx: number) =>
            idx === i
              ? { background: PURPLE, transition: { duration: 0.3 } }
              : {}
          );
          await delay(500);
        }

        await delay(2400);

        /* Fade out */
        await stepCtrl.start({ opacity: 0, transition: { duration: 0.4 } });
        await delay(300);
      }
    };
    seq();
  }, [stepCtrl, dotCtrl]);

  return (
    <div className="flex flex-col gap-0 w-full max-w-[260px] mx-auto">
      {pipelineSteps.map((step, i) => (
        <React.Fragment key={step.label}>
          <motion.div
            custom={i}
            animate={stepCtrl}
            initial={{ opacity: 0, y: 8 }}
            className="flex items-center gap-3 bg-white rounded-lg px-4 py-3 shadow-sm border border-gray-100"
          >
            <motion.div
              custom={i}
              animate={dotCtrl}
              className="w-2.5 h-2.5 rounded-full flex-shrink-0"
              style={{ background: '#e5e7eb' }}
              initial={{ background: '#e5e7eb' }}
            />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800 leading-tight">{step.label}</p>
              <p className="text-[11px] text-gray-400 leading-tight">{step.sub}</p>
            </div>
          </motion.div>

          {i < pipelineSteps.length - 1 && (
            <div className="flex justify-start pl-[18px] h-4">
              <motion.div
                className="w-px h-full"
                style={{ background: PURPLE }}
                animate={{ opacity: [0.15, 0.4, 0.15] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
              />
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

/* ─── Assemble ─── */

const panels = [<ToolConvergePanel />, <VoicePanel />, <WorkflowPanel />];

const painPoints = [
  { pain: 'Too many AI tools, no idea where to start',       payoff: 'Confident execution with a clear AI roadmap' },
  { pain: "Inconsistent output that doesn't sound like you", payoff: 'Faster, on-brand content that matches your voice' },
  { pain: 'No clear use-cases for your actual business',     payoff: 'Automated day-to-day operations that just work' },
];

const PainBridge: React.FC = () => {
  const { ref: ref1, isVisible: vis1 } = useScrollReveal(0.1);
  const { ref: ref2, isVisible: vis2 } = useScrollReveal(0.1);
  const { ref: ref3, isVisible: vis3 } = useScrollReveal(0.1);
  const refs = [ref1, ref2, ref3];
  const vis  = [vis1, vis2, vis3];

  return (
    <div style={{ background: 'linear-gradient(160deg, #07000d 0%, #0f0118 40%, #0a000f 100%)' }}>
      <div className="max-w-6xl mx-auto px-6 lg:px-8 pt-24 md:pt-32 pb-4">
        <SectionHeading
          label="Sound Familiar?"
          title="The Problems We Solve"
          description="You know AI could change everything. But right now it feels like more noise than signal."
          variant="dark"
        />
      </div>

      <div className="max-w-6xl mx-auto px-6 lg:px-8 py-12 md:py-16 space-y-8">
        {painPoints.map((item, i) => {
          const isEven = i % 2 === 0;
          return (
            <div
              key={i}
              ref={refs[i]}
              className={`transition-all duration-700 ${vis[i] ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            >
              <FeatureBlock
                reversed={!isEven}
                textContent={
                  <div className="flex flex-col gap-5">
                    <p className="text-white/30 text-xl lg:text-2xl font-bold tracking-[-0.01em] leading-snug line-through decoration-white/15">
                      {item.pain}
                    </p>
                    <h3 className="text-xl lg:text-2xl font-bold text-white tracking-[-0.01em] leading-snug">
                      {item.payoff}
                    </h3>
                  </div>
                }
                panelContent={panels[i]}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PainBridge;
